import React, { useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import dayjs from '~/timeInit';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
} from '@mui/material';

import * as LoyaltyService from '~/services/LoyaltyService';

import ApiError from '~/types/ApiError';
import {
  CUSTOM_RULE_PURPOSE_OPTIONS,
  CUSTOM_RULE_TYPE_OPTIONS,
  type CustomRuleInput,
  type CustomRulePurpose,
  type CustomRuleType,
  MAX_END_DATE,
  MIN_START_DATE,
  POINTS_TYPE_OPTIONS,
  type PointsType,
} from '~/types/loyaltyContract';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function CreateCustomRuleDialog({ open, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  // Validation errors from API
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  type IdentifierType = 'offerId' | 'packageId' | 'earnKey' | 'promoKey';
  const [selectedIdentifierType, setSelectedIdentifierType] = useState<IdentifierType>('earnKey');

  const identifierOptions = [
    { value: 'offerId', label: 'Offer ID' },
    { value: 'packageId', label: 'Package ID' },
    { value: 'earnKey', label: 'Earn Key' },
    { value: 'promoKey', label: 'Promo Key' },
  ];

  const [formData, setFormData] = useState<Partial<CustomRuleInput>>({
    type: 'BONUS_POINTS',
    purpose: 'ESTIMATION_ONLY',
    pointsType: 'BONUS_POINTS',
    productType: null,
    name: '',
    startDate: '',
    endDate: null,
  });

  const createMutation = useMutation({
    mutationFn: (rule: CustomRuleInput) => LoyaltyService.createCustomRule({ rule }),
    onSuccess: () => {
      enqueueSnackbar('Custom rule created successfully', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'custom-rules'] });
      setValidationErrors({}); // Clear any previous validation errors
      handleClose();
    },
    onError: (error: ApiError) => {
      if (error.name === 'ValidationError' && error.status === 400 && error.errors) {
        const errors: Record<string, string> = {};
        // Parse validation errors - handle both string format (Zod) and object format (schema validation)
        if (Array.isArray(error.errors)) {
          error.errors.forEach((errorItem: string | { message: string; path: string; value: string | object }) => {
            if (typeof errorItem === 'string') {
              // Zod error format: "fieldName: error message"
              const match = errorItem.match(/^(\w+):\s*(.+)$/);
              if (match) {
                const [, field, message] = match;
                errors[field] = message;
              }
            } else if (typeof errorItem === 'object' && errorItem.path && errorItem.message) {
              // Schema validation error format: { message, path, value }
              // Extract field name from path like "rule.identifierDescription"
              const pathParts = errorItem.path.split('.');
              const field = pathParts[pathParts.length - 1]; // Get last part of path
              errors[field] = errorItem.message;
            }
          });
        }
        setValidationErrors(errors);
        enqueueSnackbar('Please fix validation errors', { variant: 'error' });
      } else {
        enqueueSnackbar(`Error creating custom rule: ${error.message || 'Unknown error'}`, { variant: 'error' });
      }
    },
  });

  const handleClose = (): void => {
    setFormData({
      type: 'BONUS_POINTS_ONLY',
      purpose: 'ESTIMATION_AND_EARN',
      pointsType: 'BONUS_POINTS',
      productType: null,
      name: '',
      startDate: '',
      endDate: null,
    });
    setSelectedIdentifierType('earnKey');
    setValidationErrors({});
    onClose();
  };

  const handleSubmit = (): void => {
    if (!formData.name || !formData.startDate || !formData.type || !formData.purpose || !formData.pointsType) {
      enqueueSnackbar('Please fill in all required fields (Name, Type, Purpose, Points Type, Start Date)', {
        variant: 'error',
      });
      return;
    }

    const rule: CustomRuleInput = {
      type: formData.type,
      purpose: formData.purpose,
      pointsType: formData.pointsType,
      productType: null,
      name: formData.name,
      // Convert to Sydney timezone midnight and then to ISO 8601 UTC
      startDate: dayjs.tz(formData.startDate, 'Australia/Sydney').startOf('day').toISOString(),
      endDate: formData.endDate
        ? dayjs.tz(formData.endDate, 'Australia/Sydney').add(1, 'day').startOf('day').toISOString()
        : null,
      // Only set the selected identifier field, all others are null
      offerId: selectedIdentifierType === 'offerId' ? formData.offerId || null : null,
      packageId: selectedIdentifierType === 'packageId' ? formData.packageId || null : null,
      earnKey: selectedIdentifierType === 'earnKey' ? formData.earnKey || null : null,
      promoKey: selectedIdentifierType === 'promoKey' ? formData.promoKey || null : null,
      region: null, // Hidden field, rarely used
      identifierDescription: formData.identifierDescription || null,
      pointsCap: null, // Always null based on server rules
      pointsValue:
        formData.type === 'BONUS_POINTS_ONLY' || formData.type === 'BONUS_POINTS' ? formData.pointsValue ?? null : null,
      pointsMultiplier: formData.type === 'MULTIPLE_ON_BASE_POINTS' ? formData.pointsMultiplier ?? null : null,
      pointsRatio: null, // Always null based on server rules
      pointsValidDays: null, // Hidden field, rarely used
      pointsValidMonths: formData.type === 'BONUS_POINTS_ONLY' ? 6 : 36,
    };

    createMutation.mutate(rule);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New Custom Rule</DialogTitle>
      <DialogContent>
        <Box mt={2}>
          <Stack spacing={3}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                if (validationErrors.name) {
                  setValidationErrors({ ...validationErrors, name: '' });
                }
              }}
              error={!!validationErrors.name}
              helperText={validationErrors.name}
              required
              fullWidth
            />

            <Stack direction="row" spacing={2}>
              <FormControl fullWidth required>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Type"
                  onChange={(e: SelectChangeEvent<CustomRuleType>) => {
                    const newType = e.target.value;
                    // Type guard to ensure we have a valid CustomRuleType
                    if (
                      newType === 'BONUS_POINTS_ONLY' ||
                      newType === 'MULTIPLE_ON_BASE_POINTS' ||
                      newType === 'BONUS_POINTS'
                    ) {
                      setFormData({
                        ...formData,
                        type: newType,
                        // Auto-set purpose based on type
                        purpose: newType === 'BONUS_POINTS_ONLY' ? 'ESTIMATION_AND_EARN' : 'ESTIMATION_ONLY',
                        pointsCap: null,
                        pointsValue:
                          newType === 'BONUS_POINTS_ONLY' || newType === 'BONUS_POINTS'
                            ? formData.pointsValue
                            : undefined,
                        pointsMultiplier: newType === 'MULTIPLE_ON_BASE_POINTS' ? formData.pointsMultiplier : undefined,
                        pointsRatio: null,
                        // Reset pointsType to BONUS_POINTS (valid for all types) when type changes
                        pointsType: 'BONUS_POINTS',
                      });
                    }
                  }}
                >
                  {CUSTOM_RULE_TYPE_OPTIONS.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth required>
                <InputLabel>Purpose</InputLabel>
                <Select
                  value={formData.purpose}
                  label="Purpose"
                  disabled
                  onChange={(e: SelectChangeEvent<CustomRulePurpose>) =>
                    setFormData({ ...formData, purpose: e.target.value as CustomRulePurpose })
                  }
                >
                  {CUSTOM_RULE_PURPOSE_OPTIONS.map((purpose) => (
                    <MenuItem key={purpose} value={purpose}>
                      {purpose}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction="row" spacing={2}>
              <FormControl fullWidth required>
                <InputLabel>Points Type</InputLabel>
                <Select
                  value={formData.pointsType}
                  label="Points Type"
                  onChange={(e: SelectChangeEvent<PointsType>) =>
                    setFormData({ ...formData, pointsType: e.target.value as PointsType })
                  }
                >
                  {POINTS_TYPE_OPTIONS.filter((type) => {
                    // only allowed BONUS_POINTS, BONUS_STATUS_CREDITS for new rules
                    return type === 'BONUS_POINTS' || type === 'BONUS_STATUS_CREDITS';
                  }).map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Identifier Type</InputLabel>
                <Select
                  value={selectedIdentifierType}
                  label="Identifier Type"
                  onChange={(e: SelectChangeEvent<IdentifierType>) => {
                    const newType = e.target.value;

                    if (
                      newType === 'offerId' ||
                      newType === 'packageId' ||
                      newType === 'earnKey' ||
                      newType === 'promoKey'
                    ) {
                      setSelectedIdentifierType(newType);
                      setFormData({
                        ...formData,
                        offerId: undefined,
                        packageId: undefined,
                        earnKey: undefined,
                        promoKey: undefined,
                      });
                      const newErrors = { ...validationErrors };
                      delete newErrors.offerId;
                      delete newErrors.packageId;
                      delete newErrors.earnKey;
                      delete newErrors.promoKey;
                      setValidationErrors(newErrors);
                    }
                  }}
                >
                  {identifierOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label={identifierOptions.find((opt) => opt.value === selectedIdentifierType)?.label}
                value={formData[selectedIdentifierType] || ''}
                onChange={(e) => {
                  setFormData({ ...formData, [selectedIdentifierType]: e.target.value });

                  if (validationErrors[selectedIdentifierType]) {
                    setValidationErrors({ ...validationErrors, [selectedIdentifierType]: '' });
                  }
                }}
                error={!!validationErrors[selectedIdentifierType]}
                helperText={validationErrors[selectedIdentifierType]}
                required
                fullWidth
              />
            </Stack>

            <Stack direction="row" spacing={2}>
              {(formData.type === 'BONUS_POINTS_ONLY' || formData.type === 'BONUS_POINTS') && (
                <TextField
                  label="Points Value"
                  type="number"
                  value={formData.pointsValue ?? ''}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    setFormData({
                      ...formData,
                      pointsValue: value === 0 ? undefined : value,
                    });

                    if (validationErrors.pointsValue) {
                      setValidationErrors({ ...validationErrors, pointsValue: '' });
                    }
                  }}
                  error={!!validationErrors.pointsValue}
                  helperText={validationErrors.pointsValue}
                  required
                  fullWidth
                />
              )}
              {formData.type === 'MULTIPLE_ON_BASE_POINTS' && (
                <TextField
                  label="Points Multiplier"
                  type="number"
                  value={formData.pointsMultiplier ?? ''}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    setFormData({
                      ...formData,
                      pointsMultiplier: value === 0 ? undefined : value,
                    });

                    if (validationErrors.pointsMultiplier) {
                      setValidationErrors({ ...validationErrors, pointsMultiplier: '' });
                    }
                  }}
                  error={!!validationErrors.pointsMultiplier}
                  helperText={validationErrors.pointsMultiplier}
                  required
                  fullWidth
                />
              )}
            </Stack>

            <TextField
              label="Points Valid Months"
              type="number"
              value={formData.type === 'BONUS_POINTS_ONLY' ? 6 : 36}
              onChange={(e) => {
                const value = Number(e.target.value);
                setFormData({
                  ...formData,
                  pointsValidMonths: value === 0 ? undefined : value,
                });

                if (validationErrors.pointsValidMonths) {
                  setValidationErrors({ ...validationErrors, pointsValidMonths: '' });
                }
              }}
              disabled
              error={!!validationErrors.pointsValidMonths}
              helperText={
                formData.type === 'BONUS_POINTS_ONLY'
                  ? 'Fixed at 6 months for Bonus Points Only type'
                  : 'Fixed at 36 months for this type'
              }
              fullWidth
            />

            <TextField
              label="Identifier Description"
              value={formData.identifierDescription || ''}
              onChange={(e) => {
                setFormData({ ...formData, identifierDescription: e.target.value });

                if (validationErrors.identifierDescription) {
                  setValidationErrors({ ...validationErrors, identifierDescription: '' });
                }
              }}
              error={!!validationErrors.identifierDescription}
              helperText={validationErrors.identifierDescription || 'Description for this identifier'}
              fullWidth
            />

            <Stack direction="row" spacing={2}>
              <TextField
                label="Start Date"
                type="date"
                value={formData.startDate}
                onChange={(e) => {
                  setFormData({ ...formData, startDate: e.target.value });

                  if (validationErrors.startDate) {
                    setValidationErrors({ ...validationErrors, startDate: '' });
                  }
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: MIN_START_DATE,
                  max: MAX_END_DATE,
                }}
                error={!!validationErrors.startDate}
                helperText={`Must be after today's date`}
                required
                fullWidth
              />
              <TextField
                label="End Date"
                type="date"
                value={formData.endDate || ''}
                onChange={(e) => {
                  setFormData({ ...formData, endDate: e.target.value || null });

                  if (validationErrors.endDate) {
                    setValidationErrors({ ...validationErrors, endDate: '' });
                  }
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: formData.startDate || MIN_START_DATE,
                  max: MAX_END_DATE,
                }}
                error={!!validationErrors.endDate}
                helperText={
                  validationErrors.endDate || `Optional - Rule will be valid through the end of this date (inclusive)`
                }
                fullWidth
              />
            </Stack>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="text">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={createMutation.isPending}>
          {createMutation.isPending ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
